import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid as MuiGrid,
  Stack,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { AddOrUpdateBarcodeMappingRequest, BarcodeMappingDto } from '../../../../app/services/api/orderManagementClient';
import { Item, Uom } from '../../../../app/services/api/generated';
import { BarcodeScannerModal } from '../../../StockCount/components/BarcodeScanner/BarcodeScannerModal';
import { Camera01 } from '../../../../assets';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';

type Props = {
  storeNumber: string;
  open: boolean;
  mapping: BarcodeMappingDto | null;
  initialBarcode?: string;
  allowScanning: boolean;
  helperMessage?: string;
  onClose: () => void;
  onSave: (request: AddOrUpdateBarcodeMappingRequest) => Promise<void>;
};

const BarcodeMappingAddEditModal: FC<Props> = ({ storeNumber, open, mapping, onClose, onSave, initialBarcode, allowScanning, helperMessage }) => {
  const { t } = useTranslation('barcodeMapping');
  const { getItems, getUoms } = useNswagClient();

  const [itemNumber, setItemNumber] = useState('');
  const [barcode, setBarcode] = useState('');
  const [uom, setUom] = useState('');
  const [description, setDescription] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [itemOptions, setItemOptions] = useState<Item[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [uomOptions, setUomOptions] = useState<Uom[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const madein = undefined;
  const stock = true;
  const product = undefined;
  const sale = undefined;
  const material = undefined;
  const activity = undefined;
  const isDailyWaste = undefined;
  const sort  = '';
  const direction = '';
  const addItems = undefined;

  useEffect(() => {
    setErrorMessage('');
    setSnackbarOpen(false);
  }, [open]);

  useEffect(() => {
    setErrorMessage('');
    if (mapping) {
      const mappingItemNumber = mapping.ItemNumber ?? mapping.SKU;
      setItemNumber(mappingItemNumber);
      setBarcode(mapping.Barcode);
      setUom(mapping.UOM);
      setDescription(mapping.Name);
      fetchUomOptions(mappingItemNumber);
    } else {
      setItemNumber('');
      setBarcode(initialBarcode ? extractBarcodeFromGS1(initialBarcode) : '');
      setUom('');
      setSelectedItem(null);
      setItemOptions([]);
      setUomOptions([]);
      setSearchTerm('');
    }
  }, [mapping, open]);

  useEffect(() => {
    if (!open || mapping || !initialBarcode) {
      return;
    }

    setBarcode(extractBarcodeFromGS1(initialBarcode));
  }, [initialBarcode, open, mapping]);

  useEffect(() => {
    let isCancelled = false;
    const debounceTimer = setTimeout(() => {
      const fetchItems = async () => {
        if (isCancelled) return;
        
        try {
          setIsLoading(true);
          const response = await getItems(searchTerm, storeNumber, activity, madein, stock, sale, 
            product, material, true, isDailyWaste, undefined,sort, direction, 0, 100, addItems);
          
          // Only update state if this request hasn't been cancelled
          if (!isCancelled && response && response.data && response.data.items) {
            setItemOptions(response.data.items);
          } else if (!isCancelled) {
            setItemOptions(prev => (prev.length ? [] : prev));
          }
        } catch (error) {
          if (!isCancelled) {
            const msg =
            error && typeof error === 'object' && 'message' in error
              ? (error as Error).message
              : t('unknownError');
            setErrorMessage(msg);
          }
        } finally {
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
      };
    
      if (!mapping && searchTerm.length >= 1) {
        fetchItems();
      } else {
        setItemOptions(prev => (prev.length > 0 ? [] : prev));
        setIsLoading(false);
      }
    }, 300); // Wait 300ms after user stops typing before making the request

    return () => {
      clearTimeout(debounceTimer);
      isCancelled = true;
    };
  }, [searchTerm, mapping]);

  const fetchUomOptions = async (itemNum: string) => {
    try {
      const response = await getUoms(itemNum, storeNumber, undefined, undefined, undefined);
      if (response && response.data && response.data.uoms) {
        setUomOptions(response.data.uoms); // assuming the API returns { uoms: string[] }
      }
    } catch (error) {
      const msg =
      error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('unknownError');
      setErrorMessage(msg);
    }
  };

  function parseGS1128(raw: string): Record<string, string> | null {
    const fixedLengthAIs: Record<string, number> = {
      '00': 18, '01': 14, '02': 14, '03': 14, '04': 16,
      '11': 6, '12': 6, '13': 6, '15': 6, '16': 6, '17': 6,
      '20': 2,
    };

    const result: Record<string, string> = {};
    const GS = String.fromCharCode(0x1D);
    const s = raw.replace(/^]C1/, '').replace(GS, '');

    const parenthesisFormat = /^\(\d{2,4}\)/.test(s);

    if (parenthesisFormat) {
      const aiRegex = /\((\d{2,4})\)([^(]*)/g;
      let m: RegExpExecArray | null;
      while ((m = aiRegex.exec(s)) !== null) {
        result[m[1]] = m[2];
      }
    } else {
      let pos = 0;
      while (pos < s.length) {
        let ai: string | null = null;
        for (const len of [4, 3, 2]) {
          const candidate = s.substring(pos, pos + len);
          if (candidate.length === len) { ai = candidate; break; }
        }
        if (!ai) break;
        const ai2 = ai.substring(0, 2);
        if (fixedLengthAIs[ai2] !== undefined) ai = ai2;
        pos += ai.length;
        if (fixedLengthAIs[ai] !== undefined) {
          result[ai] = s.substring(pos, pos + fixedLengthAIs[ai]);
          pos += fixedLengthAIs[ai];
        } else {
          const gsIdx = s.indexOf('\x1D', pos);
          if (gsIdx === -1) { result[ai] = s.substring(pos); pos = s.length; }
          else { result[ai] = s.substring(pos, gsIdx); pos = gsIdx + 1; }
        }
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  }

  function extractBarcodeFromGS1(raw: string): string {
    const isGS1 = /\(\d{2,4}\)/.test(raw) || raw.startsWith(']C1') || raw.includes('\x1D');
    if (isGS1) {
      const parsed = parseGS1128(raw);
      if (parsed) return parsed['01'] ?? Object.values(parsed)[0];
    }
    return raw;
  }

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const isGS1 = /\(\d{2,4}\)/.test(raw) || raw.startsWith(']C1') || raw.includes('\x1D');
    setBarcode(isGS1 ? extractBarcodeFromGS1(raw) : raw);
  };

  useEffect(() => {
    if (selectedItem && selectedItem.itemNumber) {
      setItemNumber(selectedItem.itemNumber);
      fetchUomOptions(selectedItem.itemNumber);
    }
  }, [selectedItem]);

  useEffect(() => {
    // Allow alphanumeric, parentheses, and optionally the GS1-128 prefix
    const barcodePattern = /^[A-Za-z0-9()]+$/;
    // Optionally allow GS1-128 prefix ]C1
    const cleanedBarcode = barcode.startsWith(']C1') ? barcode.substring(3) : barcode;
    const isBarcodeValid = cleanedBarcode.trim().length >= 8 && barcodePattern.test(cleanedBarcode);
    const isValid = itemNumber.trim() !== '' && barcode.trim() !== '' && uom.trim() !== '' && isBarcodeValid;
    setIsFormValid(isValid);
  }, [itemNumber, barcode, uom]);

  const handleSave = async () => {
    setErrorMessage('');
    if (barcode.trim().length < 8) {
      setErrorMessage(t('barcodeTooShort'));
      return;
    }
    if (!uom) {
      setErrorMessage(t('pleaseSelectUom'));
      return;
    }
    const request: AddOrUpdateBarcodeMappingRequest = {
      ItemNumber: itemNumber,
      Barcode: barcode,
      Uom: uom,
    };
    if (mapping && mapping.Id) {
      request.Id = mapping.Id;
    }
    setIsLoading(true);
    try {
      await onSave(request);
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'message' in error
          ? (error as Error).message
          : t('unknownError');
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanDetected = (code: string) => {
    setBarcode(extractBarcodeFromGS1(code));
    setIsScannerOpen(false);
  };

  const handleScanError = (err: Error) => {
    setErrorMessage(err.message);
    setIsScannerOpen(false);
  };

  return (
    <>
      <Dialog open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ m: 4, p: 4 }}>
          {mapping ? t('editMapping') : t('addMapping')}
          <IconButton onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {helperMessage && (
            <Typography variant='textMD'
              sx={{ my: 4, mx: 6 }}>{helperMessage}</Typography>
          )}
          <Stack spacing={2}
            alignItems="center"
            sx={{ mt: 4 }}>
            {mapping ? (
              <TextField
                label={t('itemNumber')}
                value={`${itemNumber} - ${description}`}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  '@media (max-width: 600px)': { width: '90%' },
                }}
                disabled
              />
            ) : (
              <Autocomplete
                freeSolo
                value={selectedItem}
                options={itemOptions}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.itemNumber} - ${opt.description}`}
                onInputChange={(_, val) => setSearchTerm(val)}
                onChange={(_, val) => typeof val !== 'string' && setSelectedItem(val)}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  '@media (max-width: 600px)': { width: '90%' },
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params}
                    label={t('itemNumberOrDescription')}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                  />
                )}
              />
            )}

            <FormControl 
              sx={{
                width: '100%',
                maxWidth: '300px',
                '@media (max-width: 600px)': { width: '90%' },
              }}
              size="small">
              <InputLabel>{t('uom')}</InputLabel>
              <Select value={uom}
                label={t('uom')}
                onChange={(e) => setUom(e.target.value)}
                disabled={!itemNumber}>
                {uomOptions.map((opt) => <MenuItem key={opt.code}
                  value={opt.code}>{opt.code}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label={t('barcode')}
              value={barcode}
              onChange={handleBarcodeChange}
              sx={{
                width: '100%',
                maxWidth: '300px',
                '@media (max-width: 600px)': { width: '90%' },
              }}
              error={
                barcode.length > 0 && (
                  !/^[A-Za-z0-9()-]+$/.test(barcode.startsWith(']C1') ? barcode.substring(3) : barcode) ||
                  (barcode.startsWith(']C1') ? barcode.substring(3).length : barcode.length) < 8
                )
              }
              helperText={
                barcode.length === 0
                  ? ''
                  : !/^[A-Za-z0-9()-]+$/.test(barcode.startsWith(']C1') ? barcode.substring(3) : barcode)
                    ? t('barcodeAlphanumericOrGS1')
                    : (barcode.startsWith(']C1') ? barcode.substring(3).length : barcode.length) < 8
                      ? t('barcodeMinimumLength', { min: 8 })
                      : ''
              }
            />

            {allowScanning && <Typography>{t('or')}</Typography>}

            {allowScanning && (
              <Button
                startIcon={<Camera01 />}
                onClick={() => setIsScannerOpen(true)}
                variant="primary"
                size="lg"
              >
                {t('scanBarcode')}
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
          <Button onClick={onClose}
            variant="secondary"
            size="lg"
            sx={{ padding: '6px 16px' }}
            disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}
            variant="primary"
            size="lg"
            sx={{ padding: '6px 16px' }}
            disabled={isLoading || !isFormValid}
            startIcon={isLoading ? <CircularProgress size={15} />: undefined}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={()=>setSnackbarOpen(false)}
        severity="error" 
        message={errorMessage}
        duration={3000}
      />
      <BarcodeScannerModal
        isScannerOpen={isScannerOpen}
        closeModal={() => setIsScannerOpen(false)}
        onDetected={handleScanDetected}
        onError={handleScanError}
      />
    </>
  );
};

export default BarcodeMappingAddEditModal;
